import type { Cell } from './cell';
import { DEFAULT_CELL_STYLE, type RenderedCell } from './cellStyle';
import type { Sheet } from './sheet';
import { columnToNumber, numberToColumn } from '@/utils/utils';
import { type VecLike, Vector2 } from '@/utils/vec';

export class RectRange {
  constructor(
    public start: Vector2,
    public end: Vector2,
  ) {}

  static point(pos: Vector2): RectRange {
    return new RectRange(pos, pos);
  }

  static cols(startCol: number | Vector2, endCol: number): RectRange {
    return new RectRange(
      typeof startCol === 'number' ? new Vector2(startCol, 0) : startCol,
      new Vector2(endCol, Infinity),
    );
  }

  static rows(start: number | Vector2, endRow: number): RectRange {
    return new RectRange(
      typeof start === 'number' ? new Vector2(0, start) : start,
      new Vector2(Infinity, endRow),
    );
  }

  static all(): RectRange {
    return new RectRange(new Vector2(0, 0), new Vector2(Infinity, Infinity));
  }

  get min(): Vector2 {
    return this.start.min(this.end);
  }

  get max(): Vector2 {
    return this.start.max(this.end);
  }

  get size(): Vector2 {
    return this.max.sub(this.min).add([1, 1]);
  }

  get width(): number {
    return this.size.x;
  }

  get height(): number {
    return this.size.y;
  }

  newWithSize(size: VecLike): RectRange {
    return new RectRange(
      this.min.max(0),
      this.min.max(0).add(size).sub([1, 1]),
    );
  }

  contains(pos: Vector2): boolean {
    return (
      pos.x >= this.min.x &&
      pos.x <= this.max.x &&
      pos.y >= this.min.y &&
      pos.y <= this.max.y
    );
  }

  isOutside(): boolean {
    return this.max.x < 0 || this.max.y < 0;
  }

  inCol(col: number): boolean {
    return this.min.x <= col && col <= this.max.x;
  }

  inRow(row: number): boolean {
    return this.min.y <= row && row <= this.max.y;
  }

  isPointRange(): boolean {
    return this.min.equals(this.max);
  }

  isColRange(): boolean {
    return this.max.y === Infinity;
  }

  isRowRange(): boolean {
    return this.max.x === Infinity;
  }

  isAllRange(): boolean {
    return (
      this.min.x <= 0 &&
      this.min.y <= 0 &&
      this.max.x === Infinity &&
      this.max.y === Infinity
    );
  }

  isInfiniteRange(): boolean {
    return this.max.x === Infinity && this.max.y === Infinity;
  }

  isInfinite(): boolean {
    return this.max.x === Infinity || this.max.y === Infinity;
  }

  getCells(sheet: Sheet): Cell[] {
    const { min, max } = this;
    if (max.x === Infinity && max.y === Infinity) {
      const cells = sheet.getCells();
      if (min.y > 0 || min.x > 0) {
        return cells.filter(cell => cell.pos.y >= min.y && cell.pos.x >= min.x);
      }
      return cells;
    }
    if (max.x === Infinity) {
      const cells: Cell[] = [];
      for (let y = min.y; y <= max.y; y++) {
        cells.push(...sheet.getCellsByRow(y));
      }
      if (min.x > 0) {
        return cells.filter(cell => cell.pos.x >= min.x);
      }
      return cells;
    }
    if (max.y === Infinity) {
      const cells: Cell[] = [];
      for (let x = min.x; x <= max.x; x++) {
        cells.push(...sheet.getCellsByCol(x));
      }
      if (min.y > 0) {
        return cells.filter(cell => cell.pos.y >= min.y);
      }
      return cells;
    }
    const cells: Cell[] = [];
    for (let x = this.min.x; x <= this.max.x; x++) {
      for (let y = this.min.y; y <= this.max.y; y++) {
        const cell = sheet.getCellAt(new Vector2(x, y));
        if (!cell) continue;
        cells.push(cell);
      }
    }
    return cells;
  }

  hasCells(sheet: Sheet): boolean {
    const { min, max } = this;
    if (max.x === Infinity && max.y === Infinity) {
      const cells = sheet.getCells();
      if (min.y > 0 || min.x > 0) {
        return cells.some(cell => cell.pos.y >= min.y && cell.pos.x >= min.x);
      }
      return cells.length > 0;
    }
    if (max.x === Infinity) {
      const cells: Cell[] = [];
      for (let y = Math.max(min.y, 0); y <= max.y; y++) {
        cells.push(...sheet.getCellsByRow(y));
      }
      if (min.x > 0) {
        return cells.some(cell => cell.pos.x >= min.x);
      }
      return cells.length > 0;
    }
    if (max.y === Infinity) {
      const cells: Cell[] = [];
      for (let x = Math.max(min.x, 0); x <= max.x; x++) {
        cells.push(...sheet.getCellsByCol(x));
      }
      if (min.y > 0) {
        return cells.some(cell => cell.pos.y >= min.y);
      }
      return cells.length > 0;
    }
    for (let x = Math.max(min.x, 0); x <= max.x; x++) {
      for (let y = Math.max(min.y, 0); y <= max.y; y++) {
        const cell = sheet.getCellAt(new Vector2(x, y));
        if (!cell) continue;
        return true;
      }
    }
    return false;
  }

  /**
   * Returns the rendered cells in the range.
   *
   * - `pos` is the position of the cell relative to the top left of the range (not the top left of the sheet).
   * - `size` is the size of the cell.
   * - `cell` is the rendered cell.
   */
  *getRenderedCells(
    sheet: Sheet,
    defaultStyle = DEFAULT_CELL_STYLE,
  ): Iterable<{ pos: Vector2; size: Vector2; cell: RenderedCell }> {
    if (this.isInfinite()) return;
    const min = this.min.max(0);
    const max = this.max.max(0);

    let posY = 0;

    for (let y = min.y; y <= max.y; y++) {
      const rowHeight = sheet.getRowHeight(y);

      let posX = 0;
      for (let x = min.x; x <= max.x; x++) {
        const colWidth = sheet.getColWidth(x);
        const cell = sheet.getRenderedCellAt(new Vector2(x, y), defaultStyle);

        yield {
          pos: new Vector2(posX, posY),
          size: new Vector2(colWidth, rowHeight),
          cell,
        };

        posX += colWidth;
      }

      posY += rowHeight;
    }
  }

  *iterateCellPoses(): Iterable<Vector2> {
    if (this.isInfinite()) return;
    const min = this.min.max(0);
    const max = this.max.max(0);

    for (let y = min.y; y <= max.y; y++) {
      for (let x = min.x; x <= max.x; x++) {
        yield new Vector2(x, y);
      }
    }
  }

  /**
   * Counts the amount of columns until it hits a total size.
   */
  countCols(sheet: Sheet, totalSize: number): number {
    let count = 0;
    let total = 0;
    for (let x = Math.max(this.min.x, 0); x <= this.max.x; x++) {
      total += sheet.getColWidth(x);
      if (total > totalSize) break;
      count++;
    }
    return count;
  }

  /**
   * Counts the amount of rows until it hits a total size.
   */
  countRows(sheet: Sheet, totalSize: number): number {
    let count = 0;
    let total = 0;
    for (let y = Math.max(this.min.y, 0); y <= this.max.y; y++) {
      total += sheet.getRowHeight(y);
      if (total > totalSize) break;
      count++;
    }
    return count;
  }

  /**
   * Counts the width in pixels of this range.
   */
  countWidth(sheet: Sheet): number {
    if (this.isInfinite()) return Infinity;
    let total = 0;
    for (let x = Math.max(this.min.x, 0); x <= this.max.x; x++) {
      total += sheet.getColWidth(x);
    }
    return total;
  }

  /**
   * Counts the height in pixels of this range.
   */
  countHeight(sheet: Sheet): number {
    if (this.isInfinite()) return Infinity;
    let total = 0;
    for (let y = Math.max(this.min.y, 0); y <= this.max.y; y++) {
      total += sheet.getRowHeight(y);
    }
    return total;
  }

  countSize(sheet: Sheet): Vector2 {
    return new Vector2(this.countWidth(sheet), this.countHeight(sheet));
  }

  getColumnHeaders(): Vector2[] {
    const poses: Vector2[] = [];
    for (let x = this.min.x; x <= this.max.x; x++) {
      poses.push(new Vector2(x, this.min.y));
    }
    return poses;
  }

  getRowHeaders(): Vector2[] {
    const poses: Vector2[] = [];
    for (let y = this.min.y; y <= this.max.y; y++) {
      poses.push(new Vector2(this.min.x, y));
    }
    return poses;
  }

  getHeaderPoses(column = true): Vector2[] {
    return column ? this.getColumnHeaders() : this.getRowHeaders();
  }

  topRange(): RectRange {
    return new RectRange(this.min, new Vector2(this.max.x, this.min.y));
  }

  leftRange(): RectRange {
    return new RectRange(this.min, new Vector2(this.min.x, this.max.y));
  }

  rightRange(): RectRange {
    return new RectRange(new Vector2(this.max.x, this.min.y), this.max);
  }

  bottomRange(): RectRange {
    return new RectRange(new Vector2(this.min.x, this.max.y), this.max);
  }

  /**
   * Range above this range
   */
  upOfRange() {
    return new RectRange(
      new Vector2(this.min.x, this.min.y - 1),
      new Vector2(this.max.x, this.min.y - 1),
    );
  }

  /**
   * Range to the left of this range
   */
  leftOfRange() {
    return new RectRange(
      new Vector2(this.min.x - 1, this.min.y),
      new Vector2(this.min.x - 1, this.max.y),
    );
  }

  /**
   * Range to the right of this range
   */
  rightOfRange() {
    return new RectRange(
      new Vector2(this.max.x + 1, this.min.y),
      new Vector2(this.max.x + 1, this.max.y),
    );
  }

  /**
   * Range below this range
   */
  downOfRange() {
    return new RectRange(
      new Vector2(this.min.x, this.max.y + 1),
      new Vector2(this.max.x, this.max.y + 1),
    );
  }

  intersects(other: RectRange): boolean {
    return (
      this.min.x <= other.max.x &&
      this.max.x >= other.min.x &&
      this.min.y <= other.max.y &&
      this.max.y >= other.min.y
    );
  }

  *union(other: RectRange): Iterable<RectRange> {
    // Step 1: Convert to half-open intervals [min, max)
    const x1a = this.min.x,
      x2a = this.max.x + 1;
    const y1a = this.min.y,
      y2a = this.max.y + 1;

    const x1b = other.min.x,
      x2b = other.max.x + 1;
    const y1b = other.min.y,
      y2b = other.max.y + 1;

    // Step 2: Check for overlap or adjacency in half-open space
    const x1i = Math.max(x1a, x1b);
    const x2i = Math.min(x2a, x2b);
    const y1i = Math.max(y1a, y1b);
    const y2i = Math.min(y2a, y2b);

    const overlapsOrTouches = x1i <= x2i && y1i <= y2i;

    if (!overlapsOrTouches) {
      // No overlap or adjacency
      return (yield this, yield other);
    }

    // Step 3: Check for perfect alignment merges
    if (y1a === y1b && y2a === y2b) {
      // Perfect horizontal merge
      return yield new RectRange(
        new Vector2(Math.min(x1a, x1b), y1a),
        new Vector2(Math.max(x2a, x2b) - 1, y2a - 1),
      );
    }

    if (x1a === x1b && x2a === x2b) {
      // Perfect vertical merge
      return yield new RectRange(
        new Vector2(x1a, Math.min(y1a, y1b)),
        new Vector2(x2a - 1, Math.max(y2a, y2b) - 1),
      );
    }

    // Step 4: Subdivide Box B around the intersection zone
    yield this; // Keep Box A whole

    // Helper to create half-open bounds back as inclusive AABBs
    function* newBox(x1: number, x2: number, y1: number, y2: number) {
      if (x2 > x1 && y2 > y1) {
        yield new RectRange(new Vector2(x1, y1), new Vector2(x2 - 1, y2 - 1));
      }
    }

    // Slice B around the intersection box
    yield* newBox(x1b, x1i, y1b, y2b); // Left slice
    yield* newBox(x2i, x2b, y1b, y2b); // Right slice
    yield* newBox(x1i, x2i, y1b, y1i); // Bottom slice
    yield* newBox(x1i, x2i, y2i, y2b); // Top slice
  }

  *subtract(other: RectRange): Iterable<RectRange> {
    // Step 1: Convert to half-open intervals [min, max)
    const x1a = this.min.x,
      x2a = this.max.x + 1;
    const y1a = this.min.y,
      y2a = this.max.y + 1;

    const x1b = other.min.x,
      x2b = other.max.x + 1;
    const y1b = other.min.y,
      y2b = other.max.y + 1;

    // Step 2: Calculate the intersection in half-open space
    const x1i = Math.max(x1a, x1b);
    const x2i = Math.min(x2a, x2b);
    const y1i = Math.max(y1a, y1b);
    const y2i = Math.min(y2a, y2b);

    // If there's no overlap, A is completely unaffected by B
    const overlaps = x1i < x2i && y1i < y2i;
    if (!overlaps) {
      return yield this;
    }

    // Helper to create half-open bounds back as inclusive AABBs
    function* newBox(x1: number, x2: number, y1: number, y2: number) {
      if (x2 > x1 && y2 > y1) {
        yield new RectRange(new Vector2(x1, y1), new Vector2(x2 - 1, y2 - 1));
      }
    }

    // Step 3: Slice the parts of A that are OUTSIDE the intersection zone I
    // Left slice of A
    yield* newBox(x1a, x1i, y1a, y2a);

    // Right slice of A
    yield* newBox(x2i, x2a, y1a, y2a);

    // Bottom slice of A (restricted to the X-range of the intersection to prevent double-counting corners)
    yield* newBox(x1i, x2i, y1a, y1i);

    // Top slice of A (restricted to the X-range of the intersection to prevent double-counting corners)
    yield* newBox(x1i, x2i, y2i, y2a);
  }

  toString(): string {
    const { min, max } = this;
    if (max.x === Infinity && max.y === Infinity) {
      // normally, ALL should never exist
      if (min.x > 0 || min.y > 0) {
        return `${min.toExcelString()}:ALL`;
      }
      return 'ALL';
    }
    if (max.x === Infinity) {
      if (min.x > 0) {
        return `${min.toExcelString()}:${max.y + 1}`;
      }
      if (min.y === max.y) {
        return `${min.y + 1}`;
      }
      return `${min.y + 1}:${max.y + 1}`;
    }
    if (max.y === Infinity) {
      if (min.y > 0) {
        return `${min.toExcelString()}:${numberToColumn(max.x + 1)}`;
      }
      if (min.x === max.x) {
        return `${numberToColumn(min.x + 1)}`;
      }
      return `${numberToColumn(min.x + 1)}:${numberToColumn(max.x + 1)}`;
    }
    if (min.equals(max)) {
      return min.toExcelString();
    }
    return `${min.toExcelString()}:${max.toExcelString()}`;
  }
}

export function getRangePartType(str: string): 'point' | 'row' | 'col' | null {
  if (str.match(/^[A-Z]+\d+$/)) return 'point';
  if (str.match(/^[A-Z]+$/)) return 'col';
  if (str.match(/^\d+$/)) return 'row';
  return null;
}

export function parseRange(str: string): RectRange | null {
  const split = str.toUpperCase().split(':');
  if (split.length === 1) {
    const s = split[0]!;
    const type = getRangePartType(s);
    if (type === 'point') {
      return RectRange.point(Vector2.fromExcelString(s)!);
    } else if (type === 'row') {
      return RectRange.rows(new Vector2(0, Number(s) - 1), Number(s) - 1);
    } else if (type === 'col') {
      return RectRange.cols(
        new Vector2(columnToNumber(s) - 1, 0),
        columnToNumber(s) - 1,
      );
    }
  } else if (split.length === 2) {
    const [start, end] = split as [string, string];

    const startType = getRangePartType(start);
    const endType = getRangePartType(end);
    if (startType === 'point' && endType === 'point') {
      return new RectRange(
        Vector2.fromExcelString(start)!,
        Vector2.fromExcelString(end)!,
      );
    } else if (endType === 'row') {
      if (startType === 'point') {
        return RectRange.rows(Vector2.fromExcelString(start)!, Number(end) - 1);
      } else if (startType === 'row') {
        return RectRange.rows(
          new Vector2(0, Number(start) - 1),
          Number(end) - 1,
        );
      }
    } else if (endType === 'col') {
      if (startType === 'point') {
        return RectRange.cols(
          Vector2.fromExcelString(start)!,
          columnToNumber(end) - 1,
        );
      } else if (startType === 'col') {
        return RectRange.cols(
          new Vector2(columnToNumber(start) - 1, 0),
          columnToNumber(end) - 1,
        );
      }
    }
  }
  return null;
}
